/**
 * disclosures domain — Tier 3 server-side render + approval workflow.
 *
 * Bill 2026-05-08. Wraps the v3 production-render endpoints:
 *   - POST /v3/disclosures/resolve  — server-side equivalent of front-end DisclosureEngine.ts
 *   - POST /v3/disclosures/render   — resolve + variant alias + merge + Playwright + AcroForm
 *
 * Bill 2026-05-08 — Tier 3 M7 cutover landed; these are the canonical
 * rendering authority. The legacy client-side buildPackage path was retired
 * in the same commit (see docs/port-design/m7-cutover-audit-2026-05-08.md).
 *
 * Wave 4 Agent E (Bill 2026-05-09) — Approval workflow methods added:
 *   - POST /v3/disclosures/versions/{id}/submit
 *   - POST /v3/disclosures/versions/{id}/approve
 *   - POST /v3/disclosures/versions/{id}/reject     body: { reason }
 *   - POST /v3/disclosures/versions/{id}/activate
 *   - POST /v3/disclosures/versions/{id}/retire     body: { reason }
 *   - GET  /v3/disclosures/versions/queue
 *
 * These delegate to DisclosureApprovalFunction (see file header for the
 * role-gate matrix). Distinct from the admin authoring surface in
 * apps/risk-mgmt-panel/src/services/disclosuresAdminApi.ts which uses
 * /v3/admin/disclosures/* — that's the broader RiskManager-or-higher
 * authoring routes; this wrapper is the role-specific approval-flow
 * surface with the new Reject path.
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// ── Request / response shapes ─────────────────────────────────────────────

export interface DisclosureResolveRequest {
  cartId: string;
  audience?: string;
}

export interface ResolvedDisclosureRow {
  templateCode: string;
  title: string;
  layer?: string | null;
  required: boolean;
  requiresSignature: boolean;
  source: 'product' | 'entity' | 'universal';
  order: number;
  signerRoles: ReadonlyArray<string>;
  variantBodyTemplateCode?: string | null;
}

export interface DisclosureResolveResult {
  cartId: string;
  entityType?: string | null;
  productCategories: ReadonlyArray<string>;
  productCodes: ReadonlyArray<string>;
  resolved: ReadonlyArray<ResolvedDisclosureRow>;
  notes: ReadonlyArray<string>;
}

export interface DisclosureRenderRequest {
  cartId: string;
  templateCodes?: ReadonlyArray<string>;
  audience?: string;
  mergeDataOverrides?: Record<string, unknown>;
}

export interface RenderedDocument {
  instanceId: string;
  templateCode: string;
  bodyTemplateCode?: string | null;
  title: string;
  pdfBase64: string;
  pdfBytes: number;
  fieldCount: number;
  pageCount: number;
  contentHash: string;
  versionNumber: string;
  versionId: number;
  signerRoles: ReadonlyArray<string>;
  required: boolean;
  requiresSignature: boolean;
  tokenWarnings: ReadonlyArray<string>;
}

export interface DisclosureRenderResult {
  cartId: string;
  audience: string;
  documents: ReadonlyArray<RenderedDocument>;
  errors: ReadonlyArray<string>;
  warnings: ReadonlyArray<string>;
}

// ── Approval workflow shapes (Wave 4 Agent E) ─────────────────────────────

/**
 * Result envelope for every approval transition route. Server returns
 * `{ success: true, data: {...} }` for happy paths and `{ success: false,
 * error, code }` for state-machine guard violations / 4xx-5xx.
 */
export interface DisclosureApprovalResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

export interface DisclosureVersionTransitionData {
  versionId: number;
  templateCode: string;
  versionNumber: string;
  status: string;
  // Per-route optional stamps (whichever transition wrote them).
  submittedBy?: string | null;
  submittedAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  activatedBy?: string | null;
  activatedAt?: string | null;
  rejectedBy?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  retiredBy?: string | null;
  retiredAt?: string | null;
  retirementReason?: string | null;
}

export interface DisclosureApprovalQueueRow {
  versionId: number;
  templateCode: string;
  templateDisplayName?: string | null;
  versionNumber: string;
  status: string;
  submittedBy?: string | null;
  submittedAt?: string | null;
  authoredBy: string;
  authoredAt: string;
  pageCount: number;
  signatureCount: number;
}

export interface DisclosureApprovalQueueResponse {
  count: number;
  rows: ReadonlyArray<DisclosureApprovalQueueRow>;
}

/**
 * Optional caller-supplied `actor` and `reason` carried in the transition
 * request body. The Function endpoint resolves `actor` from the bearer
 * token's UPN when omitted, so callers typically only set `reason`
 * (required for Reject + Retire).
 */
export interface DisclosureTransitionOptions extends RequestOptions {
  actor?: string;
}

// ── Domain class ──────────────────────────────────────────────────────────

export class DisclosuresApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** Resolve the cart's disclosure list (no rendering). */
  public resolve(
    req: DisclosureResolveRequest,
    opts?: RequestOptions,
  ): Promise<DisclosureResolveResult> {
    return this.http.post<DisclosureResolveResult>('/v3/disclosures/resolve', {
      ...opts,
      body: req,
      normalizePascalToCamel: true,
    });
  }

  /** Resolve + render full package. Returns base64 PDFs + per-doc metadata. */
  public render(
    req: DisclosureRenderRequest,
    opts?: RequestOptions,
  ): Promise<DisclosureRenderResult> {
    return this.http.post<DisclosureRenderResult>('/v3/disclosures/render', {
      ...opts,
      body: req,
      normalizePascalToCamel: true,
    });
  }

  // ── Approval workflow (Wave 4 Agent E) ──────────────────────────────────
  //
  // Each method posts to /v3/disclosures/versions/{id}/{action}. Caller is
  // resolved server-side from the bearer-token UPN unless `opts.actor` is
  // supplied. Reject + Retire require `reason`. State-machine guards on the
  // server return 409 Conflict when the current Status doesn't match the
  // expected source state (caller surfaces `success=false, code='INVALID_TRANSITION'`).

  /** Draft → InReview. Fires Teams approval card to DisclosureManager queue. */
  public submitForReview(
    versionId: number,
    opts?: DisclosureTransitionOptions,
  ): Promise<DisclosureApprovalResult<DisclosureVersionTransitionData>> {
    return this.transitionPost(`/v3/disclosures/versions/${versionId}/submit`, undefined, opts);
  }

  /** InReview → Approved. DisclosureManager only. */
  public approve(
    versionId: number,
    opts?: DisclosureTransitionOptions,
  ): Promise<DisclosureApprovalResult<DisclosureVersionTransitionData>> {
    return this.transitionPost(`/v3/disclosures/versions/${versionId}/approve`, undefined, opts);
  }

  /** InReview → Draft. DisclosureManager only. Reason required. */
  public reject(
    versionId: number,
    reason: string,
    opts?: DisclosureTransitionOptions,
  ): Promise<DisclosureApprovalResult<DisclosureVersionTransitionData>> {
    return this.transitionPost(`/v3/disclosures/versions/${versionId}/reject`, reason, opts);
  }

  /** Approved → Active. Auto-supersedes prior Active version of same template. */
  public activate(
    versionId: number,
    opts?: DisclosureTransitionOptions,
  ): Promise<DisclosureApprovalResult<DisclosureVersionTransitionData>> {
    return this.transitionPost(`/v3/disclosures/versions/${versionId}/activate`, undefined, opts);
  }

  /** (any non-Retired) → Retired. Reason required. */
  public retire(
    versionId: number,
    reason: string,
    opts?: DisclosureTransitionOptions,
  ): Promise<DisclosureApprovalResult<DisclosureVersionTransitionData>> {
    return this.transitionPost(`/v3/disclosures/versions/${versionId}/retire`, reason, opts);
  }

  /** GET InReview queue rows for the DisclosureManager triage UI. */
  public listQueue(
    opts?: RequestOptions,
  ): Promise<DisclosureApprovalResult<DisclosureApprovalQueueResponse>> {
    return this.http.get<DisclosureApprovalResult<DisclosureApprovalQueueResponse>>(
      '/v3/disclosures/versions/queue',
      {
        ...opts,
        normalizePascalToCamel: true,
      },
    );
  }

  // ── Internal helpers ────────────────────────────────────────────────────

  private transitionPost(
    path: string,
    reason: string | undefined,
    opts: DisclosureTransitionOptions | undefined,
  ): Promise<DisclosureApprovalResult<DisclosureVersionTransitionData>> {
    const body: Record<string, string> = {};
    if (opts?.actor) body['actor'] = opts.actor;
    if (reason !== undefined && reason !== null) body['reason'] = reason;
    return this.http.post<DisclosureApprovalResult<DisclosureVersionTransitionData>>(path, {
      ...opts,
      body,
      normalizePascalToCamel: true,
    });
  }
}
